import { Link, Menu, MenuItem, Select, Tooltip } from '@material-ui/core';
import AnnounceKit from 'announcekit-react';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { JSX, useState } from 'react';

import {
  FreeTrialStatusEnabled,
  getMasqueradeToken,
  IPublisher,
  IUINotice,
  UINoticeActiveTrue,
} from '~/src/dash/api/console-api';
import { IconTextButton } from '~/src/dash/components/buttons/IconTextButton';
import { NoticeIcon } from '~/src/dash/components/icons/misc-icons';
import { Col, Cols, Row, Rows, spacing, Text, TextInput, TKey } from '~/src/dash/publicaUI';
import { ITProps } from '~/src/dash/publicaUI';
import {
  getDemoSSAIUsername,
  getEnv,
  getPREnabled,
  getResellerTitle,
  isOtteraMainAccounts,
  isPublica,
} from '~/src/dash/resellers/utils/importResellerConfig';
import {
  getRoutePath,
  reload,
  RouteChannelExisting,
  RouteNetworkDashboard,
  RouteSession,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateFromUTCToOffset } from '~/src/dash/utils/date';
import { checkUserInfoMasked, hasRoleSuperUser, isInternalUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { DebugBanner } from '~/src/dash/views/topbar/TopBar/DebugBanner';
import { LanguageMenu } from '~/src/dash/views/topbar/TopBar/LanguageMenu';
import { SettingsMenu } from '~/src/dash/views/topbar/TopBar/SettingsMenu';
import { ct } from '~/src/locales/common';

const masquerade = (
  username: string,
  reseller: string,
  setErr: React.Dispatch<React.SetStateAction<string>>,
): void => {
  getMasqueradeToken({
    body: {
      grant_type: 'masquerade',
      username,
      reseller,
    },
  }).then(
    (json) => {
      if ('redirect' in (json as object)) {
        location.href = (json as { redirect: string }).redirect;
      } else {
        reload(
          getDemoSSAIUsername() === username.toLowerCase() ? RouteNetworkDashboard : RouteSession,
        );
      }
    },
    (reason: Record<string, string>) => {
      setErr(reason.error);
      return;
    },
  );
};

const MasqueradeUser = (): JSX.Element => {
  const [err, setErr] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
  const [username, setUsername] = useState('');
  const onMenuClick = ({ currentTarget }: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(currentTarget);
  };

  const [selectedReseller, setSelectedReseller] = React.useState('publica');
  const handleResellerChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    setSelectedReseller(event.target.value as string);
  };

  const onMenuClose = (): void => {
    setAnchorEl(undefined);
  };

  return (
    <>
      <IconTextButton
        handleOnClick={(e: React.MouseEvent<HTMLButtonElement>) => onMenuClick(e)}
        label='Masquerade'
        iconSlug='masquerade_small'
      />
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        PaperProps={{
          style: {
            transform: 'translateX(-10%) translateY(30%)',
          },
        }}
        onClose={onMenuClose}
      >
        <Cols smallHorizontalPadding>
          <Col>
            <TextInput
              width='280px'
              enableClear
              initialValue={username}
              handleOnChange={(value: string) => setUsername(value)}
              inputSize='small'
              isAutoFocused
              placeholder='Username'
            />
          </Col>
          <Col>
            <Select
              id='reseller-select'
              value={selectedReseller}
              onChange={handleResellerChange}
              style={{
                marginLeft: '1rem',
                marginRight: '1rem',
              }}
              label={ct('Resellers')}
            >
              <MenuItem value='publica'>{ct('reseller_publica')}</MenuItem>
              <MenuItem value='ottera'>{ct('reseller_ottera')}</MenuItem>
              <MenuItem value='evrideo'>{ct('reseller_evrideo')}</MenuItem>
              <MenuItem value='orka'>{ct('reseller_orka')}</MenuItem>
            </Select>
          </Col>
          <Col>
            <IconTextButton
              handleOnClick={() => masquerade(username, selectedReseller, setErr)}
              label=''
              iconSlug='masquerade_small'
              buttonStyle={{ height: '29.5px', marginLeft: spacing.extraSmall }}
              hoverBackgroundColor={publicaColors.lightGray}
            />
          </Col>
        </Cols>
      </Menu>
      {!isEmpty(err) && (
        <SnackbarView
          open={!isEmpty(err)}
          error={true}
          onClose={() => {
            setErr('');
          }}
        >
          {err}
        </SnackbarView>
      )}
    </>
  );
};

interface IUINoticeBannerProps {
  notices: IUINotice[];
  publisherID: string;
}

const UINoticeBanner = (props: IUINoticeBannerProps): React.JSX.Element => {
  const { notices, publisherID } = props;
  const activeUiNotices = notices.filter(
    (uiNotice: IUINotice) => uiNotice.Active === UINoticeActiveTrue,
  );
  return (
    <Cols
      horizontalPadding
      extraSmallVerticalPadding
      marginBottom={spacing.small}
      gap={spacing.base}
      style={{
        backgroundColor: publicaColors.ivory,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '6px',
      }}
    >
      <Col displayFlex>
        <NoticeIcon color={publicaColors.veryDarkGray} />
      </Col>
      <Col>
        {activeUiNotices?.map((notice: IUINotice) => {
          if (notice.Name === 'Deactivate Channel Notice' && notice.Detail.length > 0) {
            const siteIDs = notice.Detail.split(',');
            return (
              <>
                <Text key={notice.ID} bold extraSmall>
                  {notice.Description + '('}
                  {siteIDs.map((siteID: string, index: number) => {
                    siteID = siteID.trim();
                    let siteIDStr = siteID;
                    if (siteIDs.length > 1 && index !== siteIDs.length - 1) {
                      siteIDStr += ',';
                    }
                    return (
                      <Link
                        style={{ marginLeft: 2, marginRight: 2 }}
                        href={
                          '#' +
                          getRoutePath(RouteChannelExisting, { p: publisherID }, { ch: siteID })
                        }
                      >
                        {siteIDStr}
                      </Link>
                    );
                  })}
                  {') | '}
                  <Link
                    style={{ marginLeft: 2, marginRight: 2 }}
                    href={
                      'https://publica.zendesk.com/hc/en-us/articles/40835619830171-Automatic-Channel-Deactivation-Policy'
                    }
                  >
                    Help
                  </Link>
                </Text>
              </>
            );
          } else {
            return (
              <Text key={notice.ID} bold extraSmall>
                {notice.Detail ? notice.Description + ': ' + notice.Detail : notice.Description}
              </Text>
            );
          }
        })}
      </Col>
    </Cols>
  );
};

const trialNoticeMessage = (numDaysRemaining: number) => {
  let message = '';
  const reseller = getResellerTitle();
  switch (true) {
    case numDaysRemaining > 2:
      message = `You are currently in Trial Mode. You have ${numDaysRemaining} days left before your trial expires. Please reach out to your ${reseller} contact if you have any questions!`;
      break;
    case numDaysRemaining > 0:
      message = `You only have ${numDaysRemaining} ${
        numDaysRemaining > 1 ? 'days' : 'day'
      } remaining on your free trial. Please reach out to your ${reseller} contact to avoid any interruption in service.`;
      break;
    default:
      message = `Your free trial has ended. Your account is now in read-only mode. Please reach out to your ${reseller} representative to continue to optimize your OTT revenue!`;
      break;
  }
  return message;
};

interface IFreeTrialNoticeProps {
  publisher: IPublisher;
}

const FreeTrialNotice = ({ publisher }: IFreeTrialNoticeProps) => {
  const { FreeTrialActive, FreeTrialStartDate, FreeTrialEndDate, Timezone } = publisher;
  if (FreeTrialActive !== FreeTrialStatusEnabled || !FreeTrialStartDate || !FreeTrialEndDate)
    return <></>;

  const today = moment().tz(Timezone);
  const numDaysRemaining = formatDateFromUTCToOffset(FreeTrialEndDate, Timezone).diff(
    today,
    'days',
  );

  return (
    <Tooltip title={trialNoticeMessage(numDaysRemaining)}>
      <div>
        <Cols gap={spacing.small} style={{ alignItems: 'center', cursor: 'help' }}>
          <Col>
            <Text extraSmall bold disableSelect>
              Free Trial
            </Text>
          </Col>
          <Col>
            <Text extraSmall bold highlighted disableSelect>
              {`${numDaysRemaining} ${numDaysRemaining === 1 ? 'Day' : 'Days'} Left`}
            </Text>
          </Col>
        </Cols>
      </div>
    </Tooltip>
  );
};

interface IProps {
  title: string;
  enableUINotices?: boolean;
  tKey?: TKey;
  tProps?: ITProps;
}

export const TopBar = ({ title, enableUINotices, tKey, tProps }: IProps): React.JSX.Element => {
  const { session, user } = useBaseStore();
  const isFreeTrialAccount = session.publisher.FreeTrialActive === FreeTrialStatusEnabled;
  const isAnnounceKitDisplayed =
    (isPublica() && getDemoSSAIUsername() !== user.user.Username) ||
    isOtteraMainAccounts(session.publisher.ID);
  const isSuperUser = hasRoleSuperUser(user.user);
  const internalUser = isInternalUser(user.user);

  return (
    <Rows>
      {/* STAGING BANNER */}
      {(getEnv() === 'staging' || getPREnabled()) && (
        <Row noPadding marginBottom={spacing.small}>
          <DebugBanner />
        </Row>
      )}
      {enableUINotices && !checkUserInfoMasked(user.user) && !isEmpty(user.uiNotices) && (
        <Row noPadding>
          <UINoticeBanner notices={user.uiNotices} publisherID={session.publisher.ID} />
        </Row>
      )}
      <Row noPadding>
        <Cols
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Col>
            <Text extraSmall muted bold tKey={tKey} tProps={tProps}>
              {title}
            </Text>
          </Col>
          <Col>
            <Cols gap={spacing.base} style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
              {isSuperUser && internalUser && (
                <Col>
                  <MasqueradeUser />
                </Col>
              )}
              {isFreeTrialAccount && (
                <Col paddingRight={spacing.large}>
                  <FreeTrialNotice publisher={session.publisher} />
                </Col>
              )}
              {isAnnounceKitDisplayed && (
                <Col>
                  {/* @ts-ignore */}
                  <AnnounceKit widget='https://announcekit.co/widgets/v2/dINjO'>
                    <IconTextButton
                      handleOnClick={() => null} // main onClick is handled by AnnounceKit.
                      iconSlug='bell_small'
                      tooltipTKey='Updates'
                    />
                  </AnnounceKit>
                </Col>
              )}
              {isSuperUser && (
                <Col>
                  <LanguageMenu />
                </Col>
              )}
              <Col>
                <SettingsMenu />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
